import type {
  ConsoleErrorEntry,
  ConsoleErrorSnapshot,
} from "./consoleErrorBuffer";

/**
 * Neither the feedback endpoint's nor its mailer's size limit was ever
 * measured (see `docs/error-tracking.md`), so the block is kept small and is
 * the only part of the report that is ever cut.
 */
export const MAX_BLOCK_CHARS = 6000;

const seconds = (ms: number, digits: number): string =>
  (ms / 1000).toFixed(digits);

const formatEntry = (entry: ConsoleErrorEntry): string => {
  const [first, ...frames] = entry.msg.split("\n");
  // Wide enough to keep a 20-minute survey (`1234.567`) aligned.
  const prefix = `[${seconds(entry.t, 3).padStart(8)}] `;
  const indent = " ".repeat(prefix.length + 2);
  const count = entry.count > 1 ? ` (x${entry.count})` : "";
  return [
    `${prefix}${first}${count}`,
    ...frames.map((frame) => `${indent}${frame}`),
  ].join("\n");
};

const heading = (shown: number, total: number, pageAgeMs: number): string => {
  const count = shown < total ? `last ${shown} of ${total}` : `${shown}`;
  return `Console errors (${count}, oldest first; page age ${seconds(pageAgeMs, 1)} s):`;
};

/**
 * The page age in the heading is what pairs an entry's time with `Sent at:`
 * and with `serverTimestamp` in `results-logs`. Over the limit the oldest
 * entries go first; the heading says how many are shown.
 */
export const formatConsoleErrorsBlock = (
  snapshot: ConsoleErrorSnapshot,
  maxChars: number = MAX_BLOCK_CHARS,
): string => {
  const {entries, total, pageAgeMs} = snapshot;
  if (entries.length === 0) return "Console errors: none";

  const lines = entries.map(formatEntry);
  let start = lines.length - 1;
  let size = lines[start].length + 1;
  while (start > 0) {
    const next = size + lines[start - 1].length + 1;
    const shown = lines.length - start + 1;
    if (heading(shown, total, pageAgeMs).length + next > maxChars) break;
    size = next;
    start -= 1;
  }
  const kept = lines.slice(start);
  return [heading(kept.length, total, pageAgeMs), ...kept].join("\n");
};
